import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { FaLink } from 'react-icons/fa';
import Navigation from './Navigation';
import AdminFormLayout from './AdminFormLayout';
import { filterMenuModuleRecords } from './outletMenuModuleRegistry';
import { normalizeOutletTypeValue, resolveOutletTypeLabel } from './outletTypeOptions';

function OutletModuleForm() {
    const { id: urlId } = useParams();
    const navigate = useNavigate();
    const [outlets, setOutlets] = useState([]);
    const [modules, setModules] = useState([]);
    const [formData, setFormData] = useState({
        outletId: '',
        selectedModules: [],
        status: '1'
    });

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            navigate('/loginadmin');
            return;
        }

        const fetchData = async () => {
            await fetchOutlets(token);
            if (urlId) {
                await fetchModuleDetailsById(token, urlId);
            }
        };
        fetchData();
    }, [urlId, navigate]);

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            return;
        }

        if (!formData.outletId) {
            setModules([]);
            return;
        }

        const selectedOutlet = outlets.find(outlet => outlet.ID === formData.outletId);
        fetchModules(token, formData.outletId, selectedOutlet?.OutletType || '');
    }, [formData.outletId, outlets]);

    useEffect(() => {
        if (!modules.length) {
            return;
        }

        const allowedModuleIds = new Set(modules.map(module => module.value));
        setFormData(prevState => ({
            ...prevState,
            selectedModules: prevState.selectedModules.filter(moduleId => allowedModuleIds.has(moduleId))
        }));
    }, [modules]);

    const handleModuleChange = (moduleId) => {
        setFormData(prevState => {
            const currentModules = [...prevState.selectedModules];
            const index = currentModules.indexOf(moduleId.toString());

            if (index === -1) {
                currentModules.push(moduleId.toString());
            } else {
                currentModules.splice(index, 1);
            }

            return { ...prevState, selectedModules: currentModules };
        });
    };

    /** ✅ Fetch Module List */
    const fetchModules = async (token, outletId = '', outletType = '') => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}modulelist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    limit: 100,
                    start: 0,
                    id_outlet: outletId ? parseInt(outletId, 10) : undefined,
                    outlet_type: outletType || undefined
                })
            });
            const data = await response.json();
            if (data.error === "0" && data.results) {
                const modulesData = filterMenuModuleRecords(data.results).map(module => ({
                    value: module.ID.toString(),
                    label: module.Modulename
                }));
                setModules(modulesData);
            }
        } catch (err) {
            console.error('Failed to fetch module list:', err);
        }
    };

    /** ✅ Fetch Outlet List */
    /** ✅ Fetch Outlet List */
const fetchOutlets = async (token) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ limit: 100, start: 0 }),
        });
        const data = await response.json();
        
        if (data.error === "0") {
            const formattedOutlets = data.results.map(outlet => ({
                ID: outlet.id.toString(),
                OutletName: outlet.outlet_name,
                OutletType: normalizeOutletTypeValue(outlet.outlet_type)
            }));
            setOutlets(formattedOutlets);
        }
    } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Failed to fetch outlets', 'error');
    }
};


    /** ✅ Fetch Module Details by ID */
    const fetchModuleDetailsById = async (token, moduleId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outlet-module-details`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ id_outlet: parseInt(moduleId, 10) })
            });

            const data = await response.json();
            if (data.error === 0 && data.data) {
                setFormData({
                    outletId: data.data.id_outlet.toString(),
                    selectedModules: data.data.modules.map(module => module.toString()),
                    status: '1'
                });
            } else {
                Swal.fire('Error', data.message || 'Failed to fetch module details', 'error');
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Failed to fetch module details', 'error');
        }
    };

    /** ✅ Handle Form Submit */
    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('sessionToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outlet-module`, {
                method: urlId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    id_outlet: parseInt(formData.outletId),
                    modules: formData.selectedModules.map(Number),
                    status: parseInt(formData.status)
                })
            });

            const data = await response.json();
            if (data.error === 0) {
                Swal.fire('Success', `Module ${urlId ? 'updated' : 'added'} successfully`, 'success');
                navigate('/outlet-modules');
            } else {
                Swal.fire('Error', data.message || `Failed to ${urlId ? 'update' : 'add'} module`, 'error');
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error', `Failed to ${urlId ? 'update' : 'add'} module`, 'error');
        }
    };

    return (
        <div>
            <Navigation />
            <AdminFormLayout
                icon={FaLink}
                eyebrow="Operational configuration"
                title={urlId ? 'Update Outlet Module Assignment' : 'Create Outlet Module Assignment'}
                description="Tetapkan modul yang berlaku pada outlet tertentu untuk menjaga konsistensi konfigurasi layanan dan operasional."
                breadcrumbs={[
                    { label: 'Dashboard', to: '/dashboardadmin' },
                    { label: 'Outlet Module Assignment', to: '/outlet-modules' },
                    { label: urlId ? 'Update Assignment' : 'Create Assignment' },
                ]}
                tipsTitle="Assignment Notes"
                tips={[
                    'Pilih outlet yang tepat sebelum menentukan daftar modul.',
                    'Pastikan modul yang ditetapkan telah sesuai dengan kebutuhan operasional outlet.',
                    'Perubahan yang disimpan akan langsung memengaruhi konfigurasi akses outlet.',
                ]}
            >
                <Form onSubmit={handleSubmit}>
                    {formData.outletId && (
                        <div className="mb-3 text-muted small">
                            Module list filtered for <strong>{resolveOutletTypeLabel(outlets.find(outlet => outlet.ID === formData.outletId)?.OutletType)}</strong> outlet type.
                        </div>
                    )}

                    <div className="field-stack">
                        <Form.Label>Outlet</Form.Label>
                        <Form.Select
                            value={formData.outletId}
                            onChange={(e) => setFormData({ ...formData, outletId: e.target.value, selectedModules: [] })}
                            required
                        >
                            <option value="">Select outlet</option>
                            {outlets.map(outlet => (
                                <option key={outlet.ID} value={outlet.ID}>
                                    {outlet.OutletName} ({resolveOutletTypeLabel(outlet.OutletType)})
                                </option>
                            ))}
                        </Form.Select>
                    </div>

                    <div className="field-stack">
                        <Form.Label>Modules</Form.Label>
                        {formData.outletId ? (
                            <div className="check-grid">
                                {modules.map((module) => (
                                    <Form.Check
                                        key={module.value}
                                        type="checkbox"
                                        label={module.label}
                                        checked={formData.selectedModules.includes(module.value)}
                                        onChange={() => handleModuleChange(module.value)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted small">Select an outlet first to load the matching modules.</div>
                        )}
                    </div>

                    <div className="form-actions">
                        <Button type="submit" variant="primary">
                            {urlId ? 'Save Changes' : 'Save Assignment'}
                        </Button>
                        <Button variant="secondary" onClick={() => navigate('/outlet-modules')}>
                            Cancel
                        </Button>
                    </div>
                </Form>
            </AdminFormLayout>
        </div>
    );
}

export default OutletModuleForm;
